import { Skeleton } from '@shigo-ui/ui';

const yTickWidths = [28, 22, 26, 20];
const xTickCount = 5;

export interface DashboardChartSkeletonProps {
  /** Announced to assistive tech; nothing is shown on screen besides the placeholder. */
  label: string;
  /** Matches the loaded chart's height so nothing moves when the data lands. */
  height?: number | undefined;
}

/**
 * Loading placeholder for a line chart: its axes, dashed grid and a faint series line in the
 * shared neutral skeleton style, without a spinner or text.
 */
export function DashboardChartSkeleton({ label, height = 340 }: DashboardChartSkeletonProps) {
  return (
    <div
      data-slot="dashboard-chart-skeleton"
      className="relative min-w-0 overflow-hidden"
      style={{ height }}
    >
      <span role="status" aria-live="polite" aria-label={label} className="sr-only">
        {label}
      </span>
      <div aria-hidden="true">
        <div className="absolute top-2 bottom-8 left-0 flex w-10 flex-col justify-between">
          {yTickWidths.map((width, index) => (
            <Skeleton key={index} className="h-2 rounded-full" style={{ width }} />
          ))}
        </div>
        <div className="absolute top-3 right-0 bottom-9 left-12 flex flex-col justify-between">
          {yTickWidths.map((_, index) => (
            <div key={index} className="border-border border-t border-dashed" />
          ))}
        </div>
        <svg
          className="absolute top-1/4 right-0 left-12 h-1/2 w-[calc(100%-3rem)]"
          viewBox="0 0 600 120"
          preserveAspectRatio="none"
        >
          <path
            className="skeleton"
            d="M0 80 C60 60 120 90 180 70 S300 30 360 55 S480 90 540 60 S600 50 600 50"
            fill="none"
            stroke="var(--skeleton-base)"
            strokeWidth="3"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <div className="absolute right-0 bottom-0 left-12 flex justify-between">
          {Array.from({ length: xTickCount }, (_, index) => (
            <Skeleton key={index} className="h-2 w-14 rounded-full" />
          ))}
        </div>
      </div>
    </div>
  );
}
